<template>
  <div class="events-layout">

    <!-- Filters -->
    <v-card flat outlined class="filters-card">
      <div class="d-flex flex-wrap align-center gap-2 pa-3">
        <v-text-field
          v-model="filterBox"
          placeholder="Box name or MAC…"
          prepend-inner-icon="mdi-magnify"
          outlined dense hide-details clearable
          style="max-width:240px"
        />
        <v-select
          v-model="filterType"
          v-bind:items="typeItems"
          item-text="text"
          item-value="value"
          label="Event type"
          outlined dense hide-details clearable
          style="max-width:160px"
        />
        <v-select
          v-model="filterGroup"
          v-bind:items="groupItems"
          item-text="text"
          item-value="value"
          label="Group"
          outlined dense hide-details clearable
          style="max-width:180px"
        />
        <v-text-field
          v-model="filterFrom"
          label="From"
          type="date"
          outlined dense hide-details clearable
          style="max-width:160px"
        />
        <v-text-field
          v-model="filterTo"
          label="To"
          type="date"
          outlined dense hide-details clearable
          style="max-width:160px"
        />
        <v-spacer />
        <v-btn icon v-on:click="loadEvents">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </v-card>

    <!-- Events table -->
    <v-card flat outlined class="table-card">
      <v-data-table
        v-bind:headers="headers"
        v-bind:items="events"
        v-bind:options.sync="options"
        v-bind:server-items-length="totalEvents"
        v-bind:loading="loading"
        v-bind:footer-props="{ itemsPerPageOptions: [25, 50, 100] }"
        fixed-header
        class="events-table"
      >
        <template v-slot:item.created="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span v-on="on" class="caption">{{ format_relative(item.created) }}</span>
            </template>
            <span>{{ dates_dateTimeFull(item.created) }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.box_name="{ item }">
          <strong>{{ item.box_name }}</strong>
        </template>

        <template v-slot:item.mac_address="{ item }">
          <span class="mono caption text--secondary">{{ item.mac_address }}</span>
        </template>

        <template v-slot:item.type="{ item }">
          <v-chip x-small v-bind:color="eventTypeColor(item.type)" dark>
            {{ item.type }}
          </v-chip>
        </template>

        <template v-slot:item.endpoint_ip="{ item }">
          <span class="mono caption">{{ item.endpoint_ip || '—' }}</span>
        </template>

        <template v-slot:item.previous_ip="{ item }">
          <span class="mono caption text--secondary">{{ item.previous_ip || '—' }}</span>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-8">
            <v-icon large class="text--disabled">mdi-history</v-icon>
            <div class="text--disabled mt-2">No events found</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

  </div>
</template>

<script>
const EVENT_TYPE_COLORS = { connect: "success", disconnect: "error", ip_change: "warning" };

export default {
  name: "Events",

  data: () => ({
    events:      [],
    totalEvents: 0,
    loading:     false,
    options:     { page: 1, itemsPerPage: 25 },
    groups:      [],
    filterBox:   "",
    filterType:  null,
    filterGroup: null,
    filterFrom:  "",
    filterTo:    "",
    typeItems: [
      { value: "connect",    text: "Connect" },
      { value: "disconnect", text: "Disconnect" },
      { value: "ip_change",  text: "IP Change" }
    ],
    headers: [
      { text: "Time",        value: "created",     sortable: false, width: 140 },
      { text: "Box Name",    value: "box_name",    sortable: false },
      { text: "MAC Address", value: "mac_address", sortable: false, width: 160 },
      { text: "Type",        value: "type",        sortable: false, width: 130 },
      { text: "Endpoint IP", value: "endpoint_ip", sortable: false, width: 140 },
      { text: "Previous IP", value: "previous_ip", sortable: false, width: 140 }
    ]
  }),

  watch: {
    options: {
      handler() { this.loadEvents(); },
      deep: true
    },
    filterBox()   { this.resetPageAndLoad(); },
    filterType()  { this.resetPageAndLoad(); },
    filterGroup() { this.resetPageAndLoad(); },
    filterFrom()  { this.resetPageAndLoad(); },
    filterTo()    { this.resetPageAndLoad(); }
  },

  computed: {
    /** Maps loaded groups to { value, text } items for the group filter dropdown. */
    groupItems() {
      return this.groups.map(g => ({ value: g.id, text: g.name }));
    }
  },

  created() {
    if (this.$route.query.box) this.filterBox = this.$route.query.box;
    this.loadGroups();
    this.loadEvents();
  },

  methods: {
    /** Loads the list of groups for the group filter dropdown. */
    loadGroups() {
      this.$server.query({ url: "groups" }).then(data => (this.groups = data)).catch(() => {});
    },

    /**
     * Loads events from the server using the current filters and pagination.
     */
    loadEvents() {
      // Sequence guard: rapid filter changes fire overlapping requests; only the
      // latest one may write results, so a slow earlier response can't overwrite it.
      const seq = (this._loadSeq = (this._loadSeq || 0) + 1);
      this.loading = true;
      const { page, itemsPerPage } = this.options;
      const params = new URLSearchParams();

      if (this.filterBox)   params.set("box",   this.filterBox);
      if (this.filterType)  params.set("type",  this.filterType);
      if (this.filterGroup) params.set("group", this.filterGroup);
      if (this.filterFrom)  params.set("from",  this.filterFrom);
      if (this.filterTo)    params.set("to",    this.filterTo);
      params.set("limit",  itemsPerPage);
      params.set("offset", (page - 1) * itemsPerPage);

      this.$server
        .query({ url: `stats/connection-log?${params.toString()}` })
        .then(result => {
          if (seq !== this._loadSeq) return;
          this.events      = result.events || [];
          this.totalEvents = result.total  || 0;
        })
        .catch(() => {
          if (seq !== this._loadSeq) return;
          this.events      = [];
          this.totalEvents = 0;
        })
        .finally(() => { if (seq === this._loadSeq) this.loading = false; });
    },

    /**
     * Resets pagination to page 1 and reloads if already on page 1.
     * Avoids double-loading when the options watcher also fires.
     */
    resetPageAndLoad() {
      if (this.options.page === 1) {
        this.loadEvents();
      } else {
        this.options.page = 1;
        // options watcher fires and calls loadEvents()
      }
    },

    /** @param {"connect"|"disconnect"|"ip_change"} type */
    eventTypeColor(type) {
      return EVENT_TYPE_COLORS[type] || "grey";
    }
  }
};
</script>

<style scoped>
.events-layout {
  height: calc(100vh - 56px);
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  padding: 20px;
  gap: 16px;
  overflow: hidden;
}

.filters-card { flex-shrink: 0; }

.table-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.events-table {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.events-table::v-deep .v-data-table__wrapper {
  flex: 1;
  overflow-y: auto;
}

.gap-2 { gap: 8px; }
</style>
